import { useEffect } from 'react';
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_RowData,
  type MRT_TableOptions,
} from 'material-react-table';
import { UseQueryResult } from '@tanstack/react-query';
import Stack from '@mui/material/Stack';
import defaultLabelFromField from 'src/compass/shared/util/defaultLabelFromField';
import { ICasMRTCellProps } from './CasMRTCellRender/ICasMRTCellProps';
import CasMRTableFilter from './_internal/CasMRTableFilter';
import { useCasMRTable } from './_internal/useCasMRTable';
import CasLoader from '../CasLoader/CasLoader';
import CasRefreshButton from '../CasButton/CasRefreshButton';

export type ModifiedMRT_ColumnDef<TData extends object = {}> = Omit<
  MRT_ColumnDef<TData>,
  'header'
> & {
  header?: MRT_ColumnDef<TData>['header'];
};

type CasMRTableProps = Partial<Omit<MRT_TableOptions<MRT_RowData>, 'columns'>> & {
  queryResult?: UseQueryResult<MRT_RowData[], any>;
  data?: MRT_RowData[];
  columns?: ModifiedMRT_ColumnDef<MRT_RowData>[];
  createColumnsFromData?: boolean;
  overrideColumns?: Record<string, Record<string, any>>;
  showToolbarCustomAction?: boolean;
  tableTitle?: Function;
  /*
    For a page whose own toolbar above the table carries a reload button (CasTableToolbar's
    refreshBtn): pass the same refetch here. MRT's full screen covers that toolbar, so the reload
    is repeated in the full-screen toolbar - the way tableTitle is.
  */
  fullScreenRefetch?: VoidFunction;
  filtersList?: string[];
  hideGroupingHeader?: boolean;
};

export default function CasMRTable({
  queryResult,
  data,
  showToolbarCustomAction = true,
  tableTitle,
  fullScreenRefetch,
  filtersList = [],
  columns,
  enableRowVirtualization = false,
  enableColumnVirtualization = false,
  enablePagination = true,
  enableExpanding = false,
  muiPaginationProps,
  initialState,
  hideGroupingHeader = false,
  ...rest
}: CasMRTableProps) {
  if (data && queryResult) throw Error("Can't specify both queryResult and data.");

  if (!data && !queryResult) throw Error('Either queryResult or data must be specified.');

  const table = useCasMRTable({
    queryResult,
    data,
    ...(showToolbarCustomAction && {
      /*
        flexGrow keeps these beside the search box (MRT spaces its toolbar's items apart).
        minHeight is the height of MRT's own toolbar buttons, which it top-aligns: centering in
        the same 40px lines the reload up with them.
      */
      renderTopToolbarCustomActions: ({ table: tableProp }: Pick<ICasMRTCellProps, 'table'>) =>
        tableProp.getState().isFullScreen && (
          <Stack direction="row" alignItems="center" gap={1} sx={{ flexGrow: 1, minHeight: 40 }}>
            {fullScreenRefetch && <CasRefreshButton refetch={fullScreenRefetch} />}
            {tableTitle && tableTitle({ table })}
          </Stack>
        ),
    }),
    enableRowVirtualization,
    enableColumnVirtualization,
    ...(columns && {
      columns: columns.map((column) => ({
        ...column,
        header: column.header ? column.header : defaultLabelFromField(column.accessorKey),
      })),
    }),
    enablePagination,
    ...(enablePagination && {
      muiPaginationProps: {
        rowsPerPageOptions: [15, 25, 100],
        ...muiPaginationProps,
      },
      paginateExpandedRows: false,
    }),
    enableExpanding,
    initialState: {
      ...(enablePagination && {
        pagination: { pageIndex: 0, pageSize: 25 },
      }),
      ...(enableExpanding && { expanded: {} }),
      ...initialState,
    },
    muiBottomToolbarProps: {
      sx: enablePagination ? undefined : { maxHeight: '1px', minHeight: '1px' }, // when not paginated, shorten bottom toolbar
    },
    ...rest,
  });

  // reset filters when api response changes
  useEffect(() => {
    if (queryResult?.isFetching) table.setColumnFilters([]);
  }, [queryResult?.isFetching, table]);

  return (
    <>
      {tableTitle && tableTitle({ table })}

      {queryResult && queryResult.isLoading ? (
        <CasLoader />
      ) : (
        <>
          {filtersList.length > 0 &&
            table.getAllColumns().length > 0 &&
            filtersList.map((columnId) => (
              <CasMRTableFilter
                columnId={columnId}
                table={table}
                resetFilters={queryResult?.isFetching ?? false}
              />
            ))}

          <div
            className={`MRTable ${
              enableRowVirtualization || enableColumnVirtualization
                ? 'MRTable-grid'
                : 'MRTable-standard'
            } 
            ${hideGroupingHeader && 'MRTable-hide-grouping-header'}
            `}
          >
            <MaterialReactTable table={table} />
          </div>
        </>
      )}
    </>
  );
}
